import get from 'lodash-es/get';
import defer from 'lodash-es/defer';
import merge from 'lodash-es/merge';
import getDisabledStyle from '../css/disabledStyle';
import updateStyle from '../css/updateStyle';
import updateClass from '../css/updateClass';
import each from 'lodash-es/each';
import {renderControls} from '../renderControls';
import { getDefaultControlStyle, getDefaultControl } from './panel.defaults';
import Control from './control';
import uniqueId from '../uniqueId';

const reverseAnimation = {
  'slide-in-down': 'slide-out-up',
  'slide-in-up': 'slide-out-down',
  'slide-in-left': 'slide-out-right',
  'slide-in-right': 'slide-out-left',
  'fade-in': 'fade-out',
  'hinge-in-from-top': 'hinge-out-from-top',
  'hinge-in-from-right': 'hinge-out-from-right',
  'hinge-in-from-bottom': 'hinge-out-from-bottom',
  'hinge-in-from-left': 'hinge-out-from-left',
  'hinge-in-from-middle-x': 'hinge-out-from-middle-x',
  'hinge-in-from-middle-y': 'hinge-out-from-middle-y',
  'scale-in-up': 'scale-out-up',
  'scale-in-down': 'scale-out-down',
  'spin-in': 'spin-out',
  'spin-in-ccw': 'spin-out-ccw',
};

const speedDuration = { fast: 250, normal: 500, slow: 750 };

export default class Panel extends Control {
  constructor(instance, path, nicePath) {
    super(instance, path, nicePath);
    this.addApi(this, 'text', () => this._checkDataOption('text', this._controlOptions.HTML), {
      formatter: 'HTMLFormatter'
    });
  }

  _clearMuiClasses() {
    if (!this.el) { return; }
    const el = this.el;
    const classes = el.className.split(' ');
    for (let i = classes.length - 1; i >= 0; i--) {
      const cls = classes[i];
      if (cls.indexOf('mui-') === 0 || reverseAnimation[cls] || Object.values(reverseAnimation).indexOf(cls) !== -1) {
        el.classList.remove(cls);
      }
    }
  }

  _revealIn() {
    const control = this;
    const options = this._controlOptions;
    const el = control.el;
    if (!el) { return; }

    if (control._animTimer) {
      clearTimeout(control._animTimer);
      delete control._animTimer;
    }

    control._clearMuiClasses();

    // Restore display if it was hidden
    if (control.options.originalDisplay !== undefined) {
      el.style.display = control.options.originalDisplay;
      delete control.options.originalDisplay;
    }

    // Override default visibility handling — we animate instead
    control._defaultShow = false;

    el.classList.add(options.revealAnimation);
    el.classList.add(options.revealAnimationTiming || 'ease-in-out');
    el.classList.add('mui-enter');

    if (options.revealAnimationSpeed && options.revealAnimationSpeed !== 'normal') {
      el.classList.add(options.revealAnimationSpeed);
    }

    _.delay(function() {
      if (control.visible) {
        el.classList.add('mui-enter-active');
        el.style.opacity = 1;
        el.style.pointerEvents = 'auto';
      }
    }, 20);
  }

  _revealOut() {
    const control = this;
    const options = this._controlOptions;
    const el = control.el;
    if (!el) { return; }

    if (control._animTimer) {
      clearTimeout(control._animTimer);
      delete control._animTimer;
    }

    const exitAnim = reverseAnimation[options.revealAnimation];
    if (!exitAnim) {
      // No reverse available — just hide immediately
      control._defaultShow = true;
      return;
    }

    // Override default visibility handling — we animate first, then hide
    control._defaultShow = false;

    control._clearMuiClasses();

    el.classList.add(exitAnim);
    el.classList.add(options.revealAnimationTiming || 'ease-in-out');
    el.classList.add('mui-leave');

    if (options.revealAnimationSpeed && options.revealAnimationSpeed !== 'normal') {
      el.classList.add(options.revealAnimationSpeed);
    }

    el.style.opacity = 1;
    el.style.pointerEvents = 'none';

    _.delay(function() {
      el.classList.add('mui-leave-active');
    }, 20);

    // After animation completes, hide the element
    const duration = speedDuration[options.revealAnimationSpeed] || 500;
    control._animTimer = setTimeout(function() {
      delete control._animTimer;
      control._clearMuiClasses();
      el.style.opacity = '';
      if (!control.visible) {
        control.options.originalDisplay = el.style.display;
        el.style.display = 'none';
      }
    }, duration + 50);
  }

  renderElement(html) {
    const control = this;
    const options = this._controlOptions;
    const instance = this.instance;
    const path = this.path;
    // console.log('renderPanel start');
    if (!control.el) {
      // console.log('renderPanel new el');
      control.el = getDefaultControl();
      var bodyEl = control.el.getElementsByClassName('body')[0];
      if(!control.id){
        control.id = uniqueId(bodyEl, 'recharts-panel-',Panel);
        control.styleSheet = 'css-' + control.id;        
      }else{
        bodyEl.setAttribute('id',control.id);
      }     

      instance.on('update', function(evt, opts) {
        defer(function() {
          let html = get(opts, path + '.HTML');
          // console.log('panel update: ' + html.substr(0, 187));
          if (html !== undefined) {
            control.render(html);
          }
        });
      });
    }

    bodyEl = control.el.getElementsByClassName('body')[0];
    var stylePatch = {};
    var defaultStyle = getDefaultControlStyle();

    merge(stylePatch, defaultStyle.normal);

    if (options.style && options.style.normal) {
      merge(stylePatch, options.style.normal);
    }

    var frameProps = [
      'top',
      'right',
      'bottom',
      'left',
      'width',
      'minWidth',
      'maxWidth',
      'height',
      'minHeight',
      'maxHeight',
      'margin',
      'position',
      'display',
      'visibility',
      'transform',
      'transition',
      'opacity',
      'clip-path',
      'zIndex',
      'pointerEvents'
    ];
    var frameStylePatch = _.pick(stylePatch, frameProps);
    frameProps.forEach(function(p) {
      if (stylePatch[p] != 'auto') {
        delete stylePatch[p];
      }
    });

    merge(frameStylePatch, control.frameStyle);

    // if (stylePatch.width != 'auto') {
    //   stylePatch.position = 'absolute';
    //   stylePatch.right = 0;
    //   stylePatch.left = 0;
    // }

    // if (stylePatch.height != 'auto') {
    //   stylePatch.position = 'absolute';
    //   stylePatch.top = 0;
    //   stylePatch.bottom = 0;
    // }

    if (control.disabled) {
      merge(stylePatch, getDisabledStyle());
      if (options.style && options.style.disabled) {
        merge(stylePatch, options.style.disabled);
      }

      if (control.isLayoutMode) {
        delete stylePatch.pointerEvents;
      }
    }

    updateStyle(control.el, frameStylePatch, control.currentFrameStyle);
    // console.log(res);
    control.currentFrameStyle = frameStylePatch;
    if (control.isLayoutMode) {
      stylePatch.pointerEvents = 'none';
    }

    updateStyle(bodyEl, stylePatch, control.currentBodyStyle);
    control.currentBodyStyle = stylePatch;
    let newHTML = html || control.text || '';

    if (control.currentHTML !== newHTML) {
      bodyEl.innerHTML = newHTML;
      control.currentHTML = newHTML;
    }

    updateClass(control.styleSheet, options.CSS, '#' + control.id);
    // console.log('renderPanel end',);

    // Apply reveal/hide animation when revealAnimation is configured
    if (options.revealAnimation && options.revealAnimation !== 'none') {
      const isVisible = control.visible;
      const wasVisible = control._animWasVisible;

      if (wasVisible === undefined) {
        // First render — just track state, no animation
        control._animWasVisible = isVisible;
      } else if (isVisible && !wasVisible) {
        control._animWasVisible = true;
        control._revealIn();
      } else if (!isVisible && wasVisible) {
        control._animWasVisible = false;
        control._revealOut();
      }
    }

    if (options.controls) {
      each(options.controls, function(ctrl) {
        ctrl.lookAndFeel = merge({}, options.lookAndFeel, ctrl.lookAndFeel);
      });
      renderControls(instance, path, control.nicePath, control.el);
    }

    return control.el;
  }
}
